import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { passwordSchema } from '@/lib/validation';
import gibletNoEyes from "@/assets/giblet-no-eyes.png";
import ispaiText from "@/assets/ispai-text.png";

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Refs for Giblet eye tracking
  const svgRef = useRef<SVGSVGElement>(null);
  const leftEyeRef = useRef<SVGCircleElement>(null);
  const rightEyeRef = useRef<SVGCircleElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoverTarget, setHoverTarget] = useState<string | null>(null);

  // Eye socket positions
  const leftEyeBaseX = 99;
  const leftEyeBaseY = 45;
  const rightEyeBaseX = 189;
  const rightEyeBaseY = 45;
  const eyeSize = 36;
  const pupilSize = 25;
  const maxOffset = 12;
  const movementDivisor = 18;

  // Track mouse movement
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Update eye positions based on mouse or hover target
  useEffect(() => {
    if (!svgRef.current || !leftEyeRef.current || !rightEyeRef.current) return;

    const svgRect = svgRef.current.getBoundingClientRect();
    let targetX = mousePosition.x;
    let targetY = mousePosition.y;

    // Override with hover target if set
    if (hoverTarget) {
      let targetRect: DOMRect | undefined;
      switch (hoverTarget) {
        case 'password':
          targetRect = passwordInputRef.current?.getBoundingClientRect();
          break;
        case 'confirmPassword':
          targetRect = confirmPasswordInputRef.current?.getBoundingClientRect();
          break;
        case 'submitButton':
          targetRect = submitButtonRef.current?.getBoundingClientRect();
          break;
      }
      if (targetRect) {
        targetX = targetRect.left + targetRect.width / 2;
        targetY = targetRect.top + targetRect.height / 2;
      }
    }

    const calculateEyePosition = (eyeBaseX: number, eyeBaseY: number) => {
      const eyeCenterX = svgRect.left + (eyeBaseX + eyeSize / 2) * (svgRect.width / 334);
      const eyeCenterY = svgRect.top + (eyeBaseY + eyeSize / 2) * (svgRect.height / 206);

      const deltaX = targetX - eyeCenterX;
      const deltaY = targetY - eyeCenterY;

      const offsetX = Math.max(-maxOffset, Math.min(maxOffset, deltaX / movementDivisor));
      const offsetY = Math.max(-maxOffset, Math.min(maxOffset, deltaY / movementDivisor));

      return { offsetX, offsetY };
    };

    const leftEyePosition = calculateEyePosition(leftEyeBaseX, leftEyeBaseY);
    const rightEyePosition = calculateEyePosition(rightEyeBaseX, rightEyeBaseY);

    leftEyeRef.current.setAttribute('cx', String(leftEyeBaseX + eyeSize / 2 + leftEyePosition.offsetX));
    leftEyeRef.current.setAttribute('cy', String(leftEyeBaseY + eyeSize / 2 + leftEyePosition.offsetY));

    rightEyeRef.current.setAttribute('cx', String(rightEyeBaseX + eyeSize / 2 + rightEyePosition.offsetX));
    rightEyeRef.current.setAttribute('cy', String(rightEyeBaseY + eyeSize / 2 + rightEyePosition.offsetY));
  }, [mousePosition, hoverTarget]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords match
    if (password !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive"
      });
      return;
    }

    // Validate password strength
    const validation = passwordSchema.safeParse(password);
    if (!validation.success) {
      toast({
        title: "Validation Error",
        description: validation.error.issues[0].message,
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      toast({
        title: "Password Updated",
        description: "Your password has been successfully reset. Please log in.",
      });

      // Redirect to auth page after short delay
      setTimeout(() => {
        navigate('/auth');
      }, 1500);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update password. This link may have expired.",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex flex-col items-center mb-4 space-y-2">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <svg ref={svgRef} width="167" height="103" viewBox="0 0 334 206" className="animate-fade-in">
                <image href={gibletNoEyes} width="334" height="206" x="0" y="0" />
                <circle 
                  ref={leftEyeRef}
                  cx={leftEyeBaseX + eyeSize / 2} 
                  cy={leftEyeBaseY + eyeSize / 2} 
                  r={pupilSize / 2} 
                  fill="black"
                  style={{ transition: 'all 0.15s ease-out' }}
                />
                <circle 
                  ref={rightEyeRef}
                  cx={rightEyeBaseX + eyeSize / 2} 
                  cy={rightEyeBaseY + eyeSize / 2} 
                  r={pupilSize / 2} 
                  fill="black"
                  style={{ transition: 'all 0.15s ease-out' }}
                />
              </svg>
            </motion.div>

            <motion.img
              src={ispaiText}
              alt="iSPAI"
              className="h-10 object-contain"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            />
          </div>
          <CardTitle className="text-2xl text-center">
            Reset Your Password
          </CardTitle>
          <CardDescription className="text-center">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                ref={passwordInputRef}
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setHoverTarget('password')}
                onBlur={() => setHoverTarget(null)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                ref={confirmPasswordInputRef}
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setHoverTarget('confirmPassword')}
                onBlur={() => setHoverTarget(null)}
                required
              />
            </div>
            <Button 
              ref={submitButtonRef}
              type="submit" 
              variant="active" 
              className="w-full" 
              disabled={loading}
              onMouseEnter={() => setHoverTarget('submitButton')}
              onMouseLeave={() => setHoverTarget(null)}
            >
              {loading ? 'Updating...' : 'Reset Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
